import { SaplingMark } from '../components/ui/SaplingMark'
import { RolePicker } from '../components/settings/RolePicker'
import { useState } from 'react'
import { joinCodeFromSearch } from '@aurora/core'
import { ConvexClient } from 'convex/browser'
import { useAuthActions } from '@convex-dev/auth/react'
import { api } from '@aurora/convex/convex/_generated/api'
import { getToken, setToken } from '../sync/ConvexSync'
import { authReactClient } from '../sync/auth'
import { seedLocalDemo } from '../lib/localDemo'
import { Landing } from './Landing'

/**
 * First run: sign in, start a family, or join one with a code.
 *
 * One account is one family. The second parent joins with a six-character
 * code the first parent reads to them — no invite emails. Each parent picks
 * how they appear in the app (mom, dad, or anything else).
 *
 * Sign-in runs through Convex Auth: the `password` provider first, then the
 * `legacy-password` fallback that verifies pre-migration PBKDF2 hashes and
 * rehashes them — so parents from before the migration stay signed in with
 * the password they have. Sign-up is two steps: the auth account, then
 * `completeSignUp` names the parent and creates or joins the family.
 */

type Step = 'choose' | 'signin' | 'forgot' | 'reset' | 'create' | 'join' | 'family' | 'child' | 'code'

/**
 * The provider applies the fresh JWT to the react client a beat after
 * signIn resolves; retry the first authed call briefly instead of failing
 * the whole flow on the race. Production redacts server error messages, so
 * this retries every failure — the mutations behind it are idempotent, and a
 * real error (a wrong family code) just repeats to the same answer.
 */
async function authedCall<T>(fn: () => Promise<T>): Promise<T> {
  let last: unknown
  for (let i = 0; i < 5; i++) {
    try {
      return await fn()
    } catch (e: unknown) {
      last = e
      await new Promise((r) => setTimeout(r, 300))
    }
  }
  throw last
}

export function Welcome({ convexUrl, needsChild, needsFamily }: {
  convexUrl: string
  needsChild?: boolean
  /** Signed in through Convex Auth, but no family picked or joined yet. */
  needsFamily?: boolean
}) {
  // An invite link carries the family code: open on the join form with it typed.
  const [invitedCode] = useState(() => joinCodeFromSearch(window.location.search))
  const [step, setStep] = useState<Step>(
    needsChild ? 'child' : needsFamily ? 'family' : invitedCode ? 'join' : 'choose',
  )
  // Undefined only when there is no Convex URL (local demo), where nothing
  // below the demo button runs.
  const auth = useAuthActions() as ReturnType<typeof useAuthActions> | undefined
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [joinCode, setJoinCode] = useState<string | null>(null)

  // form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [label, setLabel] = useState('mom')
  const [code, setCode] = useState(invitedCode ?? '')
  const [resetCode, setResetCode] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [childName, setChildName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [pronouns, setPronouns] = useState<'she' | 'he' | 'they'>('they')

  const client = () => new ConvexClient(convexUrl)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e: any) {
      setError(String(e?.message ?? e).replace(/^.*Error: /, ''))
      setBusy(false)
    }
  }

  // The Password provider keys the account by the email exactly as sent, so
  // every flow normalizes the way the legacy backend did.
  const normEmail = () => email.trim().toLowerCase()

  const signIn = () =>
    run(async () => {
      try {
        await auth!.signIn('password', { email: normEmail(), password, flow: 'signIn' })
      } catch {
        try {
          // Pre-migration parents: verify the PBKDF2 hash and rehash. The
          // errors collapse into one on purpose — no probing which emails
          // exist, and no telling which scheme a parent is on.
          await auth!.signIn('legacy-password', { email, password })
        } catch {
          throw new Error('Email or password is not right.')
        }
      }
      window.location.reload()
    })

  const requestReset = () =>
    run(async () => {
      // Pre-migration parents need a password account row before the OTP
      // flow can find them; this mints a bare one and answers ok either way.
      await authReactClient!.mutation(api.authApi.ensureResetable, { email })
      try {
        await auth!.signIn('password', { email: normEmail(), flow: 'reset' })
      } catch {
        /* the notice reads the same whether or not the email exists */
      }
      setNotice(`If ${email.trim()} has an account, a six-character code is on its way.`)
      setStep('reset')
      setBusy(false)
    })

  const reset = () =>
    run(async () => {
      if (password.length < 8) throw new Error('Password needs at least 8 characters.')
      try {
        await auth!.signIn('password', {
          email: normEmail(),
          code: resetCode,
          newPassword: password,
          flow: 'reset-verification',
        })
      } catch {
        throw new Error('That code is not right or has expired. Ask for a new one.')
      }
      window.location.reload()
    })

  /** The auth account, created or (on a retry) signed back into. */
  const ensureAuthed = async () => {
    try {
      await auth!.signIn('password', { email: normEmail(), password, flow: 'signUp' })
    } catch {
      try {
        await auth!.signIn('password', { email: normEmail(), password, flow: 'signIn' })
      } catch {
        throw new Error('Could not create the account. If that email already has one, sign in instead.')
      }
    }
  }

  const create = () =>
    run(async () => {
      if (password.length < 8) throw new Error('Password needs at least 8 characters.')
      await ensureAuthed()
      const res = await authedCall(() =>
        authReactClient!.mutation(api.authApi.completeSignUp, { name, label }),
      )
      setJoinCode(res.joinCode ?? null)
      setStep('child')
      setBusy(false)
    })

  const join = () =>
    run(async () => {
      if (password.length < 8) throw new Error('Password needs at least 8 characters.')
      await ensureAuthed()
      await authedCall(() =>
        authReactClient!.mutation(api.authApi.completeSignUp, { name, label, joinCode: code }),
      )
      window.location.reload()
    })

  /** The family step for a signed-in parent who has none yet (reload mid-signup). */
  const completeFamily = () =>
    run(async () => {
      const res = await authedCall(() =>
        authReactClient!.mutation(api.authApi.completeSignUp, {
          name,
          label,
          joinCode: code.trim() ? code : undefined,
        }),
      )
      if (res.joinedExisting) {
        window.location.reload()
        return
      }
      setJoinCode(res.joinCode ?? null)
      setStep('child')
      setBusy(false)
    })

  const addChild = () =>
    run(async () => {
      const P = {
        she: { subject: 'she', object: 'her', possessive: 'her' },
        he: { subject: 'he', object: 'him', possessive: 'his' },
        they: { subject: 'they', object: 'them', possessive: 'their' },
      }[pronouns]
      const child = {
        name: childName,
        birthDate,
        pronouns: P,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      const legacyToken = getToken()
      if (legacyToken && legacyToken !== 'local-demo') {
        const c = client()
        await c.mutation(api.authApi.addChild, { token: legacyToken, ...child })
      } else {
        await authedCall(() =>
          authReactClient!.mutation(api.authApi.addChild, { token: '', ...child }),
        )
      }
      if (joinCode) setStep('code')
      else window.location.reload()
      setBusy(false)
    })

  const startDemo = () =>
    run(async () => {
      if (!convexUrl) {
        setToken('local-demo')
        seedLocalDemo()
        window.location.reload()
        return
      }
      const c = client()
      const { token } = await c.mutation(api.seed.createDemoAccount, {})
      setToken(token)
      window.location.reload()
    })

  if (step === 'choose') {
    return (
      <>
        <Landing busy={busy} onSignIn={() => setStep('signin')} onCreate={() => setStep('create')}
          onJoin={() => setStep('join')} onDemo={startDemo} />
        {error && <p className="welcome-error landing-error">{error}</p>}
      </>
    )
  }

  return (
    <div className="welcome">
      <div className="welcome-inner">
        <div className="welcome-mark" aria-hidden="true">
          <SaplingMark size={72} tone="brand" />
        </div>

        {step === 'signin' && (
          <AuthForm
            title="Welcome back"
            fields={
              <>
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  onKeyDown={(e) => e.key === 'Enter' && signIn()} />
              </>
            }
            action="Sign in" busy={busy} onSubmit={signIn} onBack={() => setStep('choose')}
            extra={
              <button className="btn btn-quiet btn-sm" onClick={() => { setNotice(null); setStep('forgot') }}>
                Forgot your password?
              </button>
            }
          />
        )}

        {step === 'forgot' && (
          <AuthForm
            title="Forgot your password?"
            hint="Type the email you signed up with and we will send a six-character code."
            fields={
              <input className="field" type="email" placeholder="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                onKeyDown={(e) => e.key === 'Enter' && requestReset()} />
            }
            action="Send the code" busy={busy} onSubmit={requestReset} onBack={() => setStep('signin')}
          />
        )}

        {step === 'reset' && (
          <AuthForm
            title="Choose a new password"
            hint={notice ?? 'Type the code from the email and a new password.'}
            fields={
              <>
                <input className="field field-code" placeholder="A1B2C3"
                  autoComplete="one-time-code" maxLength={6} value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/[^0-9a-z]/gi, '').toUpperCase())} />
                <input className="field" type="password" placeholder="New password (8+ characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" onKeyDown={(e) => e.key === 'Enter' && reset()} />
              </>
            }
            action="Set password and sign in" busy={busy} onSubmit={reset}
            onBack={() => setStep('forgot')}
            extra={
              <button className="btn btn-quiet btn-sm" onClick={requestReset} disabled={busy}>
                Send a new code
              </button>
            }
          />
        )}

        {step === 'create' && (
          <AuthForm
            title="Start your family"
            fields={
              <>
                <input className="field" placeholder="Your name" value={name}
                  onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <RolePicker value={label} onChange={setLabel} />
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password (8+ characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" />
              </>
            }
            action="Create account" busy={busy} onSubmit={create} onBack={() => setStep('choose')}
          />
        )}

        {step === 'join' && (
          <AuthForm
            title="Join your family"
            hint={invitedCode
              ? 'Your invite filled in the family code. Add your name and a password to join.'
              : 'Ask the parent who set things up for the six-character family code.'}
            fields={
              <>
                <input className="field field-code" placeholder="FAMILY CODE" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} />
                <input className="field" placeholder="Your name" value={name}
                  onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <RolePicker value={label} onChange={setLabel} />
                <input className="field" type="email" placeholder="Email" value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <input className="field" type="password" placeholder="Password (8+ characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" />
              </>
            }
            action="Join family" busy={busy} onSubmit={join} onBack={() => setStep('choose')}
          />
        )}

        {step === 'family' && (
          <AuthForm
            title="Almost there"
            hint="You are signed in. Now name yourself and start your family, or type the code from the parent who already did."
            fields={
              <>
                <input className="field" placeholder="Your name" value={name}
                  onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <RolePicker value={label} onChange={setLabel} />
                <input className="field field-code" placeholder="FAMILY CODE (if joining)" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} />
              </>
            }
            action="Continue" busy={busy} onSubmit={completeFamily}
          />
        )}

        {step === 'child' && (
          <AuthForm
            title="Who is this for?"
            hint="Pronouns are yours to choose. Nothing is ever guessed from a name."
            fields={
              <>
                <input className="field" placeholder="Child's name" value={childName}
                  onChange={(e) => setChildName(e.target.value)} />
                <input className="field" type="date" value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)} aria-label="Birth date" />
                <div className="seg">
                  {(['she', 'he', 'they'] as const).map((p) => (
                    <button key={p}
                      className={`seg-item${pronouns === p ? ' is-active' : ''}`}
                      onClick={() => setPronouns(p)}>
                      {p === 'she' ? 'she/her' : p === 'he' ? 'he/him' : 'they/them'}
                    </button>
                  ))}
                </div>
              </>
            }
            action="Add child" busy={busy} onSubmit={addChild}
            onBack={needsChild ? undefined : () => setStep('choose')}
          />
        )}

        {step === 'code' && (
          <>
            <h1 className="welcome-title">You're in</h1>
            <p className="welcome-body">
              To add the other parent, have them choose <strong>Join with a code</strong> and
              type this:
            </p>
            <p className="join-code">{joinCode}</p>
            <p className="welcome-body">It's also in the Child tab whenever you need it.</p>
            <div className="welcome-actions">
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Open Sapling
              </button>
            </div>
          </>
        )}

        {error && <p className="welcome-error">{error}</p>}
      </div>
    </div>
  )
}

function AuthForm({
  title, hint, fields, action, busy, onSubmit, onBack, extra,
}: {
  title: string
  hint?: string
  extra?: React.ReactNode
  fields: React.ReactNode
  action: string
  busy: boolean
  onSubmit: () => void
  onBack?: () => void
}) {
  return (
    <>
      <h1 className="welcome-title" style={{ fontSize: 'var(--text-xl)' }}>{title}</h1>
      {hint && <p className="welcome-body">{hint}</p>}
      <div className="auth-fields">{fields}</div>
      <div className="welcome-actions" style={{ flexDirection: 'column', gap: 'var(--s-2)' }}>
        <button className="btn btn-primary" onClick={onSubmit} disabled={busy}>
          {busy ? 'One moment…' : action}
        </button>
        {extra}
        {onBack && <button className="btn btn-quiet" onClick={onBack}>Back</button>}
      </div>
    </>
  )
}

